import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser((u) => u || r.data)).catch(() => setUser((u) => u || false));
  }, []);

  useEffect(() => {
    if (!user) return;
    const check = () => api.get("/auth/me").then((r) => setUser(r.data)).catch((e) => {
      if (e.response?.status === 401) {
        localStorage.removeItem("snd_token");
        setUser(false);
        toast.error(e.response?.data?.detail || "Session ended. Please sign in again.");
      }
    });
    const t = setInterval(check, 60 * 60 * 1000);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVisible); };
  }, [user]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem("snd_token", data.access_token);
    setUser(data);
  }, []);

  const employeeLogin = useCallback(async (emp_id, password) => {
    const { data } = await api.post("/auth/employee", { emp_id, password });
    if (data.access_token) localStorage.setItem("snd_token", data.access_token);
    setUser(data);
  }, []);

  const ssoLogin = useCallback(async (code) => {
    const { data } = await api.post("/auth/sso", { code });
    if (data.access_token) localStorage.setItem("snd_token", data.access_token);
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    if (data.access_token) localStorage.setItem("snd_token", data.access_token);
    setUser(data);
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("snd_token");
    setUser(false);
  }, []);

  return <AuthContext.Provider value={{ user, login, employeeLogin, ssoLogin, register, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
